import {
  imageContentType,
  isAcceptedImage,
  putStoredFileBytes,
  sha256Base64,
} from "../../lib/storedFileUpload";
import {
  completeProcedurePhotoUpload,
  createProcedurePhotoUpload,
  ProcedurePhotoRequestError,
  type ProcedurePhotoScope,
  retryProcedurePhotoUpload,
} from "../../queries/useProcedurePhotos";

/** The most photos one PHOTO item may hold; the server enforces the same. */
export const MAX_PHOTOS_PER_ITEM = 10;

export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";

export const PHOTO_LIMIT_MESSAGE =
  "A photo item can have up to 10 photos. Remove a photo or choose fewer.";
export const PHOTO_TYPE_MESSAGE =
  "Choose JPEG, PNG, or WebP images up to 25 MB.";

export type PhotoUploadPhase =
  | "queued"
  | "hashing"
  | "staging"
  | "uploading"
  | "verifying"
  | "saving"
  | "failed";

/**
 * How a failed photo can recover: re-sign the same upload session, start a
 * new session, resend only the save, or nothing (it can only be removed).
 */
export type PhotoRetryKind = "session" | "new-session" | "save" | "none";

export type PhotoUploadEntry = {
  localId: string;
  itemId: string;
  file: File;
  previewUrl: string;
  uploadId: string;
  /** True once the server has staged this upload id. */
  staged: boolean;
  checksum: string | null;
  phase: PhotoUploadPhase;
  error?: { message: string; retry: PhotoRetryKind };
};

export function isUploadInProgress(entry: PhotoUploadEntry) {
  return entry.phase !== "saving" && entry.phase !== "failed";
}

export function createPhotoEntry(itemId: string, file: File): PhotoUploadEntry {
  return {
    localId: crypto.randomUUID(),
    itemId,
    file,
    previewUrl:
      typeof URL.createObjectURL === "function"
        ? URL.createObjectURL(file)
        : "",
    uploadId: crypto.randomUUID(),
    staged: false,
    checksum: null,
    phase: "queued",
  };
}

export function revokePreview(entry: PhotoUploadEntry) {
  if (entry.previewUrl && typeof URL.revokeObjectURL === "function")
    URL.revokeObjectURL(entry.previewUrl);
}

/**
 * Checks a new selection for one item before anything uploads. The whole
 * selection is refused when it would take the item past the photo limit;
 * otherwise unsupported files are dropped with a message.
 */
export function validatePhotoSelection(files: File[], slotsUsed: number) {
  if (slotsUsed + files.length > MAX_PHOTOS_PER_ITEM)
    return { accepted: [] as File[], error: PHOTO_LIMIT_MESSAGE };
  const accepted = files.filter(isAcceptedImage);
  return {
    accepted,
    error: accepted.length === files.length ? null : PHOTO_TYPE_MESSAGE,
  };
}

/** Maps an upload-step failure to the recovery the server allows. */
export function classifyUploadError(error: unknown): {
  message: string;
  retry: PhotoRetryKind;
} {
  const message =
    error instanceof Error && error.message
      ? error.message
      : "Unable to upload the photo.";
  if (/expired/i.test(message)) return { message, retry: "new-session" };
  if (
    error instanceof ProcedurePhotoRequestError &&
    (error.status === 403 || error.status === 404)
  )
    return { message, retry: "none" };
  if (
    /cannot be retried|no longer available|discarded|not allowed|size limit|unsupported characters|different metadata/i.test(
      message,
    )
  )
    return { message, retry: "none" };
  return { message, retry: "session" };
}

type StepReporter = (
  phase: PhotoUploadPhase,
  patch?: Partial<PhotoUploadEntry>,
) => void;

/**
 * Stages (or re-signs) the upload, PUTs the bytes to S3 when needed, then asks
 * the server to verify them. Resuming an entry that is already staged uses the
 * retry route with the same upload id, so no duplicate file is created.
 */
export async function uploadPhotoEntry(
  scope: ProcedurePhotoScope,
  entry: PhotoUploadEntry,
  report: StepReporter,
) {
  const contentType = imageContentType(entry.file);
  if (!contentType) throw new Error(PHOTO_TYPE_MESSAGE);
  let checksum = entry.checksum;
  if (!checksum) {
    report("hashing");
    checksum = await sha256Base64(entry.file);
  }
  report("staging", { checksum });
  const session = entry.staged
    ? await retryProcedurePhotoUpload(scope, entry.uploadId)
    : await createProcedurePhotoUpload(scope, {
        uploadId: entry.uploadId,
        fileName: entry.file.name,
        contentType,
        contentLength: entry.file.size,
        checksumSha256: checksum,
      });
  report("uploading", { staged: true });
  if (!session.isUploaded) {
    if (!session.uploadUrl)
      throw new Error("The upload URL is unavailable; retry the upload.");
    await putStoredFileBytes(
      session.uploadUrl,
      entry.file,
      checksum,
      contentType,
    );
  }
  report("verifying");
  await completeProcedurePhotoUpload(scope, entry.uploadId);
}
